import { DeviceComplianceDetailDTO } from '@hem/portal-query'
import { Poppins } from '@next/font/google'
import { getMonth, getYear } from 'date-fns'
import dayjs from 'dayjs'
import { FieldMetaProps } from 'formik'
import { isArray, toUpper } from 'lodash'

const APIS = {
  accounts: {
    token: '/accounts/token',
    myProfile: '/accounts/my-profile',
    refreshToken: '/accounts/refresh-token',
    requestOtp: '/accounts/request-otp-code',
    verifyOtp: '/accounts/verify-otp-code'
  },
  configurations: {
    maintenanceStatus: '/configurations/maintenance-mode/status'
  },
  libraries: '/libraries',
  categories: '/categories',
  client: '/clients',
  asyncTaskStatus: '/async-task-status'
}

export const PORTAL_ROUTER = {
  HOME: {
    path: '/home#all',
    title: 'Home'
  },
  VIDEO_LIBRARY: {
    path: '/video-library',
    title: 'Video Library'
  },
  CATEGORY: {
    path: '/manage-category',
    title: 'Category'
  },
  MANAGE_DEVICE: {
    path: '/manage-device',
    title: 'Manage Device'
  },
  RPM_DEVICES: {
    path: '/manage-device/rpm-devices',
    title: 'RPM Devices'
  },
  NON_RPM_DEVICES: {
    path: '/manage-device/non-rpm-devices',
    title: 'Non-RPM Devices'
  },
  RPM_DEVICE_MODELS: {
    path: '/manage-device/rpm-devices/device-models',
    title: 'RPM Device Models'
  },
  SETTINGS: {
    path: '/settings',
    title: 'Settings'
  },
  RPM_INVENTORY_SUMMARY: {
    path: '/manage-device/rpm-devices/inventory-summary',
    title: 'RPM Inventory Summary'
  },
  NON_RPM_DEVICE_MODELS: {
    path: '/manage-device/non-rpm-devices/device-models',
    title: 'Non RPM Device Models'
  },
  NON_RPM_INVENTORY_SUMMARY: {
    path: '/manage-device/non-rpm-devices/inventory-summary',
    title: 'Non RPM Inventory Summary'
  },
  CLIENT_LIST: {
    path: '/client-list',
    title: 'Client List'
  },
  USER_AND_PERMISSION: {
    path: '/user-and-permission',
    title: 'User And Permission'
  },
  ABOUT: {
    path: '/about',
    title: 'About'
  },
  USER_LIST: {
    path: '/user-and-permission/user-list',
    title: 'User List'
  },
  USER_DETAIL: {
    path: '/user-and-permission/user-detail',
    title: 'User Detail'
  },
  USER_PERMISSION: {
    path: '/user-and-permission/user-permission',
    title: 'User Permission'
  },
  ACCESS_DENIED: {
    path: '/access-denied',
    title: 'Access Denied'
  },
  NOT_FOUND: {
    path: '/404',
    title: 'Not Found'
  },
  MAINTENANCE: {
    path: '/maintenance',
    title: 'Maintenance'
  },
  ROOT: {
    path: '/',
    title: ''
  },
  KNOWLEDGE_BASE_SETTING: {
    path: '/settings/knowledge-base',
    title: 'Setting'
  }
}

export const ROLES = {
  PROVIDER: {
    id: 'provider',
    display: 'Provider'
  },
  NURSE: {
    id: 'nurse',
    display: 'Nurse'
  },
  CLINICAL_MANAGER: {
    id: 'clinical_manager',
    display: 'Clinical manager'
  },
  SPECIALISTS: {
    id: 'specialists',
    display: 'Specialists'
  },
  CEM: {
    id: 'cem',
    display: 'Client Engagement Manager'
  },
  CARE_CIRCLE: {
    id: 'care_circle',
    display: 'Care Circle',
  },
  ADMIN: {
    id: 'admin',
    display: 'Admin'
  },
  BILLER: {
    id: 'biller',
    display: 'Biller'
  },
  IT_SUPPORT: {
    id: 'it_support',
    display: 'IT Support'
  }
}

// Thresholds
export const REQUIRED_FIELD = 'This field is required.'
export const VALID_DECIMAL =
  'Please enter a valid decimal number: Only one digit after the decimal point.'
export const ONE_DIGIT_AFTER_COMMA_REGEX = /^\d+(\.\d{0,1})?$/
export const NUMBER_REGEX = /^\d+$/
export const MINUS_KEY = '-'
export const PLUS_KEY = '+'
export const POSITIVE_MESSAGE = 'Value must be greater than or equal to 0.'

export const ASYNC_TASK_STATUS_KEY = 'async_tasks'

export const MAX_HIGH_THRESHOLDS = 9999999999999


export const MEASUREMENT_KEYS = {
  LOW: 'low',
  LOW_LOW: 'lowLow',
  HIGH: 'high',
  HIGH_HIGH: 'highHigh'
}

export const PERMISSIONS = {
  EDUCATION_LIBRARY: {
    VIEW_VIDEO_LIST: 'education_library_view_videos_list',
    VIEW_ASSIGN_CLIENTS_LIST: 'education_library_view_assigned_clients_list',
    ASSIGN_UNASSIGN_VIDEO: 'education_library_assign_unassigned_videos',
    BUTTON_ASSIGN_ALL_CLIENTS: 'education_library_button_assign_all_clients',
    BUTTON_REMOVE_ALL_CLIENTS: 'education_library_button_remove_all_clients',
    ADD_VIDEO: 'education_library_add_video',
    UPDATE_VIDEO: 'education_library_update_video',
    DELETE_VIDEO: 'education_library_delete_video',
    ADD_CATEGORY: 'education_library_add_category',
    DELETE_CATEGORY: 'education_library_delete_category',
    UPDATE_CATEGORY: 'education_library_update_category',
    VIEW_CATEGORY: 'education_library_view_category',
  },

  VITAL_MANAGEMENT: {
    VIEW_THRESHOLDS: 'vital_mgmt_view_thresholds',
    VIEW_VITAL_SIGN_READINGS: 'vital_mgmt_view_vital_sign_readings',
    ADD_NOTES: 'vital_mgmt_add_notes',
    VIEW_INACTIVE_ALERT_NOTES: 'vital_mgmt_view_notes',
    CONFIGURE_THRESHOLD: 'vital_mgmt_configure_update_thresholds',
    INSERT_MANUAL_READING: 'vital_mgmt_insert_manual_readings',
    ACTIVE_DEACTIVE_READING: 'vital_mgmt_activate_deactivate_readings',
    ACKNOWLEDGE_VITAL_ALERTS: 'vital_mgmt_acknowledge_vital_alert'
  },

  RPM_MANAGEMENT: {
    VIEW_DEVICES: 'rpm_mgmt_view_devices',
    VIEW_INVENTORY_SUMMARY: 'rpm_mgmt_view_inventory_summary',
    VIEW_DEVICE_MODELS: 'rpm_mgmt_view_device_models',
    CREATE_DEVICE: 'rpm_mgmt_create_device',
    UPDATE_DEVICE: 'rpm_mgmt_update_device',
    DELETE_DEVICE: 'rpm_mgmt_delete_device',
  },

  NON_RPM_MANAGEMENT: {
    VIEW_DEVICES: 'non_rpm_mgmt_view_devices',
    VIEW_INVENTORY_SUMMARY: 'non_rpm_mgmt_view_inventory_summary',
    VIEW_DEVICE_MODELS: 'non_rpm_mgmt_view_device_models',
    CREATE_DEVICE: 'non_rpm_mgmt_create_device',
    UPDATE_DEVICE: 'non_rpm_mgmt_update_device',
    DELETE_DEVICE: 'non_rpm_mgmt_delete_device',
  },

  COMPLIANCE_MANAGEMENT: {
    VIEW_COMPLIANCE_REPORT: 'compliance_mgmt_view_compliance_report',
    ACKNOWLEDGE_MISSED_COMPLIANCE_ALERT: 'compliance_mgmt_acknowledge_compliance_missed',
    VIEW_COMPLIANCE_SCHEDULE: 'compliance_mgmt_view_compliance_schedule',
    CREATE_DEVICE_COMPLIANCE_SCHEDULE: 'compliance_mgmt_create_compliance_schedule',
    DELETE_DEVICE_COMPLIANCE_SCHEDULE: 'compliance_mgmt_delete_compliance_schedule',
    PUBLISH_UNPUBLISH_COMPLIANCE_SCHEDULE: 'compliance_mgmt_publish_unpublish_compliance_schedule',
    CHANGE_COMPLIANCE_SCHEDULE_OPT_STATUS: 'compliance_mgmt_change_compliance_schedule_opt_status',
    EDIT_DEVICE_COMPLIANCE_GOALS: 'compliance_mgmt_edit_device_compliance_goals'
  },

  EDUCATION_MANAGEMENT: {
    CHANGE_EDUCATION_OPT_STATUS: 'education_mgmt_change_education_opt_status'
  },

  MEDICATION_ADHERENCE: {
    VIEW_MEDICATION_ADHERENCE_REPORT: 'medication_adherence_view_adherence_details',
    VIEW_DEVICE_EVENTS: 'medication_adherence_view_dose_device_event',
    ACKNOWLEDGE_MISSED_MEDICATION_ALERT: 'medication_adherence_acknowledge_medication_missed',
    CHANGE_MEDICATION_TAKEN_STATUS: 'medication_adherence_change_medication_taken_status',
    VIEW_MED_REMINDER: 'medication_adherence_view_medication_reminder_time',
    CREATE_MED_REMINDER: 'medication_adherence_create_medication_reminder_time',
    UPDATE_MED_REMINDER: 'medication_adherence_update_medication_reminder_time',
    DELETE_MED_REMINDER: 'medication_adherence_delete_medication_reminder_time',
    CHANGE_MED_REMINDER_OPT_STATUS: 'medication_adherence_change_medication_reminder_opt_status'
  },

  CLIENT_MANAGEMENT: {
    VIEW_ALL_CLIENTS: 'client_mgmt_view_all_clients',
    VIEW_ASSIGNED_CLIENTS: 'client_mgmt_view_assigned_clients',
    VIEW_CLIENT_CREDENTIAL: 'client_mgmt_view_client_credential',
    CREATE_CLIENT_CREDENTIAL: 'client_mgmt_create_client_credential',
    UPDATE_CLIENT_CREDENTIAL: 'client_mgmt_update_client_credential',
    VIEW_CLIENT_CONFIGURATION: 'client_mgmt_view_client_configuration',
    UPDATE_CLIENT_CONFIGURATION: 'client_mgmt_update_client_configuration',

    //RPM Device
    VIEW_ASSIGNED_DEVICES: 'client_mgmt_view_assigned_devices_of_client',
    ASSIGN_UNASSIGN_DEVICE: 'client_mgmt_assign_unassign_device_to_from_client',
    UPDATE_ASSIGNED_DEVICE_INFO: 'client_mgmt_update_assigned_device_info_of_client',

    //non-rpm-device
    ASSIGN_UNASSIGN_NON_RPM_DEVICE: 'client_mgmt_assign_unassign_non_rpm_device_to_from_client',
    UPDATE_ASSIGNED_NON_RPM_DEVICE_INFO: 'client_mgmt_update_assigned_non_rpm_device_info_of_client',
    VIEW_ASSIGNED_NON_RPM_DEVICES: 'client_mgmt_view_assigned_non_rpm_devices_of_client',

    FILTER_CLIENT_BY_CES_OR_NURSE: 'client_mgmt_filter_client_by_ces_or_nurse',
  },

  PERSONAL_EVENT_REMINDER: {
    VIEW_EVENT: 'personal_event_reminder_view_event',
    CREATE_EVENT: 'personal_event_reminder_create_event',
    UPDATE_EVENT: 'personal_event_reminder_update_event',
    DELETE_EVENT: 'personal_event_reminder_delete_event'
  },

  USER_MANAGEMENT: {
    VIEW_ALL_PERMISSIONS: 'user_mgmt_view_all_permissions',
    VIEW_ALL_PORTAL_ACCOUNTS: 'user_mgmt_view_all_portal_accounts'
  },

  SETTING: {
    VIEW: 'setting_view',
    VIEW_EVENT_TYPE: 'setting_view_event_types',
    EDIT_EVENT_TYPE: 'setting_edit_event_types',
    REFRESH_EVENT_TYPE: 'setting_refresh_event_types',
    VIEW_KNOWLEDGE_BASE_CONTENT: 'setting_view_knowledge_base_content',
    CREATE_KNOWLEDGE_BASE_CONTENT: 'setting_create_knowledge_base_content',
    UPDATE_KNOWLEDGE_BASE_CONTENT: 'setting_update_knowledge_base_content',
    DELETE_KNOWLEDGE_BASE_CONTENT: 'setting_delete_knowledge_base_content',
    VIEW_NON_RPM_DEVICE_TYPE: 'setting_non_rpm_view_device_type',
    CREATE_NON_RPM_DEVICE_TYPE: 'setting_non_rpm_create_device_type',
    UPDATE_NON_RPM_DEVICE_TYPE: 'setting_non_rpm_update_device_type',
    DELETE_NON_RPM_DEVICE_TYPE: 'setting_non_rpm_delete_device_type',
    VIEW_NON_RPM_DEVICE_MODEL: 'setting_non_rpm_view_device_model',
    CREATE_NON_RPM_DEVICE_MODEL: 'setting_non_rpm_create_device_model',
    UPDATE_NON_RPM_DEVICE_MODEL: 'setting_non_rpm_update_device_model',
    DELETE_NON_RPM_DEVICE_MODEL: 'setting_non_rpm_delete_device_model',
    VIEW_QUESTIONNAIRE: 'setting_view_questionnaire',
    REFRESH_QUESTIONNAIRE: 'setting_refresh_questionnaire',
  },

  INVENTRACER: {
    ACCESS: 'inventracer_access'
  }
}

export default APIS

// error message
export const DEFAULT_CREATE_SUCCESS = 'Your data has been created successfully!'
export const DEFAULT_UPDATE_SUCCESS = 'Your data has been updated successfully!'
export const DEFAULT_DELETE_SUCCESS = 'Your data has been deleted successfully!'
export const DEFAULT_ERROR_MSG = 'Something went wrong. Please try again later.'

// video
export const MIN_VIDEO_TITLE = 1
export const MAX_VIDEO_TITLE = 100
export const MIN_VIDEO_DESCRIPTION = 20
export const MAX_VIDEO_DESCRIPTION = 5000
export const MIN_VIDEO_TAG = 2
export const MAX_VIDEO_TAG = 30
export const REQUIRED_FIELD_MESSAGE = 'This field is required.'
export const INVALID_URL_MESSAGE =
  'Please enter another link. This video URL has already existed or invalid.'
export const VIDEO_NOT_FOUND_MESSAGE =
  'Video URL is not found on YouTube. Please try another link.'
export const VIDEO_NOT_AUTHORIZED_MESSAGE =
  'This video is not authorized for use according to permissions defined on YouTube.'
export const UNSUPPORTED_VIDEO_TYPE_MESSAGE =
  'This video URL has included "/short" or "/live" type which is not supported. Please enter another link or change video type to "/watch" in the current link.'
export const UNIQ_VIDEO_TITLE_ERROR_MESSAGE = 'A video with this title already exists.'
export const MIN_CATEGORY_NAME = 2
export const MIN_CATEGORY_NAME_ERROR_MESSAGE = 'Category name must be at least 2 characters.'
export const DUPLICATE_CATEGORY_NAME_ERROR_MESSAGE = 'This category name is duplicated.'
export const SIDE_BAR_WIDTH = 200
// client
export const USERNAME_REGEX = /^(?=.{8,20}$)[a-zA-Z0-9]+([._](?![._])|[a-zA-Z0-9]){0,18}[a-zA-Z0-9]$/
export const USERNAME_MIN_CHARS = 8
export const USERNAME_MAX_CHARS = 20
export const MAC_ADDRESS_REGEX = /^[a-fA-F0-9:]*$/
export const MAC_ADDRESS_MIN_LENGTH = 12
export const MAC_ADDRESS_MAX_LENGTH = 17

export const USERNAME_ERROR_MSG = 'Please enter a valid Username.'
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/
export const PASSWORD_MIN_CHARS = 8
export const PASSWORD_ERROR_MSG = 'Please enter a valid Password.'
export const RE_PASSWORD_ERROR_MSG = 'Password and Re-enter password does not match.'
export const SEARCH_DEBOUNCE_TIME = 1000

export const REQUIRED_TIMEZONE_MESSAGE =
  'GMT -5 time zone has been automatically assigned. Please confirm the timezone for the client before using this feature.'

export const parseClientPhone = (phone: string | number) => {
  if (phone) {
    return phone.toString().slice(0, 3) + '-' + phone.toString().slice(3, 6) + '-' + phone.toString().slice(6)
  }
  return phone
}

// user health score
export const HEALTH_FILTERS = [
  'summary',
  'vitals',
  'medication adherence',
  'compliance',
  'thresholds',
]


export interface HealthColor {
  color: string
  fill: string
  stroke?: string
  background: string
  backgroundStroke?: string
  text?: string
  legend?: string
}

export interface HealthColorProps {
  [key: string]: HealthColor
}

const FILL_WHITE = 'fill-white'

export const CHART_COLORS: HealthColorProps = {
  POOR: {
    color: 'text-red-600',
    fill: 'fill-red-600',
    stroke: 'stroke-red-600',
    background: 'fill-red-100',
    backgroundStroke: 'stroke-red-100',
    legend: 'bg-red-600',
    text: 'Poor',
  },
  FAIR: {
    color: 'text-orange-600',
    fill: 'fill-orange-600',
    stroke: 'stroke-orange-600',
    background: 'fill-orange-100',
    backgroundStroke: 'stroke-orange-100',
    legend: 'bg-orange-600',
    text: 'Fair',
  },
  GOOD: {
    color: 'text-yellow-600',
    fill: 'fill-yellow-600',
    stroke: 'stroke-yellow-600',
    background: 'fill-yellow-100',
    backgroundStroke: 'stroke-yellow-100',
    legend: 'bg-yellow-600',
    text: 'Good',
  },
  VERY_GOOD: {
    color: 'text-green-600',
    fill: 'fill-green-600',
    stroke: 'stroke-green-600',
    background: 'fill-green-100',
    backgroundStroke: 'stroke-green-100',
    legend: 'bg-green-600',
    text: 'Very Good',
  },
  EXCELLENT: {
    color: 'text-darkgreen-600',
    fill: 'fill-darkgreen-600',
    stroke: 'stroke-darkgreen-600',
    background: 'fill-darkgreen-100',
    backgroundStroke: 'stroke-darkgreen-100',
    legend: 'bg-darkgreen-600',
    text: 'Excellent',
  },
  NO_DATA: {
    color: '',
    fill: 'fill-gray-100',
    stroke: 'fill-gray-100',
    background: 'fill-gray-100',
    legend: '',
    backgroundStroke: 'fill-gray-100',
    text: 'No Data',
  },
  NOT_APPLICABLE: {
    color: '',
    fill: 'fill-gray-100',
    stroke: 'fill-gray-100',
    background: 'fill-gray-100',
    legend: '',
    backgroundStroke: 'fill-gray-100',
    text: 'Not Applicable',
  },
}

export const HEALTH_TREND_COLORS: HealthColorProps = {
  HEALTH_SCORE: {
    color: '#c084fc',
    fill: 'fill-purple-400',
    stroke: 'stroke-purple-400',
    background: 'fill-purple-100',
    backgroundStroke: 'stroke-purple-100',
    legend: 'bg-purple-400',
    text: 'Health Score',
  },
  VITALS_SCORE: {
    color: '#f472b6',
    fill: 'fill-pink-400',
    stroke: 'stroke-pink-400',
    background: 'fill-pink-100',
    backgroundStroke: 'stroke-pink-100',
    legend: 'bg-pink-400',
    text: 'Vitals Score',
  },
  MEDICATION_ADHERENCE_SCORE: {
    color: '#5eead4',
    fill: 'fill-teal-300',
    stroke: 'stroke-teal-300',
    background: 'fill-teal-100',
    backgroundStroke: 'stroke-teal-100',
    legend: 'bg-teal-300',
    text: 'Medication Adherence Score',
  },
  DEVICE_COMPLIANCE_SCORE: {
    color: '#2563eb',
    fill: 'fill-blue-600',
    stroke: 'stroke-blue-600',
    background: 'fill-blue-100',
    backgroundStroke: 'stroke-blue-100',
    legend: 'bg-blue-600',
    text: 'Device Compliance Score',
  },
  EDUCATION_SCORE: {
    color: 'rgb(133 77 14)',
    fill: 'fill-yellow-800',
    stroke: 'stroke-yellow-800',
    background: 'fill-yellow-100',
    backgroundStroke: 'stroke-yellow-100',
    legend: 'bg-yellow-800',
    text: 'Education Score',
  }
}

export const NO_DATA = 'NO_DATA'
export const NOT_APPLICABLE = 'NOT_APPLICABLE'
export const THRESHOLD_COLORS: HealthColorProps = {
  RISK: {
    color: 'text-red-600',
    fill: 'fill-red-600',
    background: 'fill-red-100',
    text: 'Risk',
    legend: 'bg-red-600',
  },
  MODERATE: {
    color: 'text-yellow-600',
    fill: 'fill-yellow-600',
    background: 'fill-yellow-100',
    text: 'Moderate',
    legend: 'bg-yellow-600',
  },
  NORMAL: {
    color: 'text-green-600',
    fill: 'fill-green-600',
    background: 'fill-green-100',
    text: 'Normal',
    legend: 'bg-green-600',
  },
}

const TRANSPARENT = FILL_WHITE

export const THRESHOLD_CHART_FILL_COLOR = [
  THRESHOLD_COLORS.RISK.fill,
  TRANSPARENT,
  THRESHOLD_COLORS.MODERATE.fill,
  TRANSPARENT,
  THRESHOLD_COLORS.NORMAL.fill,
  TRANSPARENT,
  THRESHOLD_COLORS.MODERATE.fill,
  TRANSPARENT,
  THRESHOLD_COLORS.RISK.fill,
]

export const FILTERED_DATE_OPTIONS = [
  {
    label: 'Last 7 days',
    value: 7,
  },
  {
    label: 'Last 30 days',
    value: 30,
  },
  {
    label: 'Last 90 days',
    value: 90,
  },
]

export const DATE_RANGE_90_DAYS = 90

export const DATE_RANGE_180_DAYS = 180

export const SORT_OPTIONS = [
  {
    label: 'Date Assigned',
    value: 'DATE_ASSIGNED',
  },
  {
    label: 'Date Watched',
    value: 'DATE_WATCHED',
  },
  {
    label: 'Most Watched',
    value: 'MOST_WATCHED',
  },
]

export const UNDER_SCORE = '_'
export const SPACE = ' '

export const MONTH_DAY_YEAR_HALF_DATE_TIME = 'MM/dd/yyyy - hh:mm a'
export const MONTH_DAY_YEAR_HALF_DATE_TIME_SECONDARY = 'MMM dd, yyyy - h:mm a'
export const YEAR_MONTH_DAY = 'yyyy-MM-dd'
export const HOUR_MINUTE_HALF_DATE_PRIMARY = 'hh:mm A'
export const HOUR_MINUTE_PRIMARY = 'hh:mm a'
export const HOUR_MINUTE_SECOND = 'HH:mm:ss'
export const HOUR_MINUTE = 'HH:mm'
export const MONTH_DAY_YEAR_PRIMARY = 'MM/dd/yyyy'
export const MONTH_DAY_YEAR_SECONDARY = 'MM/DD/YYYY'
export const SERVER_DATE_FORMAT = 'MM-dd-yyyy'
export const NOTE_TYPE = {
  NOTE: 'NOTE',
  ACTIVE_READING: 'ACTIVATE_READING',
  INACTIVE_READING: 'INACTIVATE_READING'
}

export const GLUCOMETER_TYPE = 'GLUCOMETER'
export const INTEGER_VALUE_DEVICE_TYPES = {
  BLOOD_PRESSURE: 'BLOOD_PRESSURE',
  GLUCOMETER: GLUCOMETER_TYPE,
  PULSE_OXIMETER: 'PULSE_OXIMETER'
}

export const GLUCOMETER_TIME_OPTIONS = [
  {
    label: 'General',
    value: 'GENERAL'
  }, {
    label: 'Before Meal',
    value: 'BEFORE_MEAL'
  }, {
    label: 'After Meal',
    value: 'AFTER_MEAL'
  }
]

export const READING_TAKEN_TIME = 'readingTakenTime'
export const VITALS_TAB_KEYS = {
  ACTIVATE: 'activate',
  INACTIVATE: 'inactivate',
}

export const MAX_LENGTH_MESSAGE = 'The max length of characters is reached. Must be 1000 characters or less.'

export const ACTIVATED_SUCCESS_MESSAGE = 'Data activated successfully!'
export const INACTIVATED_SUCCESS_MESSAGE = 'Data inactivated successfully!'
export const SAVE_DATA_SUCCESS_MESSAGE = 'Your data is saved successfully!'
export const ACKNOWLEDGE_SUCCESS_MESSAGE = 'Your data is updated successfully!'
export const INIT_ASYNC_TASK_SUCCESS_MESSAGE = 'Success! Your action was initiated, but it may take a little while to process. Refresh the page after a few minutes to see the update.'
export const ASYNC_TASK_FAILED_MESSAGE = 'An error occurred while processing your request. Please try again.'
export const ACKNOWLEDGE_ALL_SUCCESS_MESSAGE = 'All medication alerts have been acknowledged successfully.'
export const CHANGE_STATUS_ALL_SUCCESS_MESSAGE = 'All unacknowledged missed medications have been updated to taken status successfully.'
export const ACKNOWLEDGE_ALL_VITAL_ALERTS_SUCCESS_MESSAGE = 'All vital alerts for this device have been acknowledged successfully.'

export const getErrorMessage = (error: any) => {
  return (
    error.response?.data?.errors
      ?.map((error: any) => error.message)
      ?.join(', ') || DEFAULT_ERROR_MSG
  )
}

export const CLIENT_DETAIL_TABS = {
  SUMMARY: {
    hash: 'summary',
    title: 'summary'
  },
  VITALS: {
    hash: 'vitals',
    title: 'vitals'
  },
  MEDICATION_ADHERENCE: {
    hash: 'medication-adherence',
    title: 'medication adherence',
  },
  COMPLIANCE: {
    hash: 'compliance',
    title: 'device compliance',
  },
  EDUCATION: {
    hash: 'education',
    title: 'education',
  },
  THRESHOLDS: {
    hash: 'thresholds',
    title: 'thresholds',
  },
  PERSONAL_EVENT_REMINDER: {
    hash: 'personal-event-reminder',
    title: 'personal event reminder',
  },
  DEVICES: {
    hash: 'devices',
    title: 'devices'
  },
  TV_APP: {
    hash: 'tv-app-detail',
    title: 'TV App'
  }
}

export const ADHERENCE_STATUS = {
  MISSED: 'Missed',
  AWAITING_DATA: 'Awaiting Data',
  TAKEN: 'Taken'
}

export const REMINDER_TAKEN_BY = {
  DOSE_DEVICE: 'DOSE_DEVICE',
  TV_APP: 'TV_APP',
  MOBILE_APP: 'MOBILE_APP',
}

export const PAGING_DEFAULT_DATA = {
  SIZE: 25,
  PAGE_NUMBER: 0,
  PAGE_OPTIONS: [25, 50, 75, 100]
}

export const DEFAULT_ITEMS_PER_PAGE_KNOWLEDGE_BASE = 20

export const getMsgMinFieldLen = (len: number) => {
  return `The minimum value of this field is ${len}.`
}

export const getMsgMaxFieldLen = (len: number) => {
  return `The maximum value of this field is ${len}.`
}

export const PERSONAL_EVENT_REMINDER_TIME_MESSAGE = 'The max length of characters is reached. Must be 500 characters or less.'

// device schedule
export const SCHEDULE_UPDATE_SUCCESS_MESSAGE =
  'Your data is updated successfully!'

export const FREQUENCY_OPTIONS = [
  {
    label: 'Day',
    value: 'Day',
  },
  {
    label: 'Week',
    value: 'Week',
  },
]
export const SWITCH_COLOR = '#52C41A'
export const MIN_DAY_FREQ = 1
export const MAX_DAY_FREQ = 4
export const MIN_WEEK_FREQ = 1
export const MAX_WEEK_FREQ = 5

export const CLIENT_DEVICE_TYPE_OPTIONS = [
  {
    label: 'All Devices',
    value: 'ALL_DEVICES',
  },
  {
    label: 'Thermometer',
    value: 'THERMOMETER',
  },
  {
    label: 'Pulse Oximeter',
    value: 'PULSE_OXIMETER',
  },
  {
    label: 'Blood Pressure',
    value: 'BLOOD_PRESSURE',
  },
  {
    label: 'Weight Scale',
    value: 'WEIGHT_SCALE',
  },
  {
    label: 'Glucometer',
    value: 'GLUCOMETER',
  },
]

export const DEVICE_COMPLIANCE_STATUS = [
  {
    label: 'Expected Readings by Reminder Schedule',
    color: 'bg-needRefactor-500'
  },
  {
    label: 'Some Readings by Reminder Schedule',
    color: 'bg-yellow-600'
  },
  {
    label: 'No Readings by Reminder Schedule',
    color: 'bg-red-600'
  }, {
    label: 'Waiting for Readings',
    color: 'bg-neutral-500'
  }
]

// Personal reminder
export const MAX_PERSONAL_REMINDER_TITLE = 500
export const MAX_PERSONAL_REMINDER_NOTE = 1000
export const MAX_TIME_SLOT = 4
export const PERSONAL_REMINDER_EVENT_TABS = ['Upcoming', 'History']
export const TIME_ZONE_OFFSET_IN_MINUTE = dayjs().utcOffset()
export const END_TIME_VALIDATE_ERROR_MSG = 'End Time should be after Start Time'


export const getCalendar = (data: DeviceComplianceDetailDTO[]) => {
  const calendars: any[] = []

  data.forEach((item) => {

    // Safari doesn't accept '-' in date string. All browsers accept date strings with '/'
    const fromDate = (item.fromDate as string).replaceAll('-', '/')

    const fromMonth = getMonth(new Date(fromDate))
    const fromYear = getYear(new Date(fromDate))

    const fromDateObj = { month: fromMonth, year: fromYear }

    if (
      !calendars.some(
        (date) => date.month === fromMonth && date.year === fromYear
      )
    ) {
      calendars.push(fromDateObj)
    }

    const toDate = (item.toDate as string).replaceAll('-', '/')

    const toMonth = getMonth(new Date(toDate))
    const toYear = getYear(new Date(toDate))

    const toDateObj = { month: toMonth, year: toYear }

    if (
      !calendars.some((date) => date.month === toMonth && date.year === toYear)
    ) {
      calendars.push(toDateObj)
    }
  })

  return calendars
}

export const getMaxLengthErrorMsg = (length: number) => `The max length of characters is reached. Must be ${length} characters or less.`

export const getMinLengthErrorMsg = (length: number) => `This field must be ${length} characters or more.`

// Manage Device
export const DEVICE_STATUS_OPTION = [
  {
    label: 'All',
    value: 'ALL',
    default: true
  },
  {
    label: 'Active',
    value: 'active'
  },
  {
    label: 'Available',
    value: 'available',
  },
  {
    label: 'Unavailable',
    value: 'unavailable'
  }
]

export const DEVICE_SEARCH_FILTER = [
  {
    label: 'By Serial Number',
    value: 'serialNumber',
    default: true
  },
  {
    label: 'By Dose ID',
    value: 'doseId'
  },
  {
    label: 'By Model ID',
    value: 'modelId'
  },
  {
    label: 'By Mac Address',
    value: 'macAddress'
  },
  {
    label: 'By Note',
    value: 'note'
  },
  {
    label: 'By Manufacturer',
    value: 'vendor'
  }
]

export const DEVICE_SEARCH_FILTER_NON_RPM = [
  {
    label: 'By Serial Number',
    value: 'serialNumber',
    default: true
  },
  {
    label: 'By Model ID',
    value: 'modelId'
  },
  {
    label: 'By Note',
    value: 'note'
  }
]

export const GREEN_600 = '#52C41A'
export const BLUE_600 = '#1890FF'
export const GRAY_300 = '#BDC1CA'

export const MAX_DEVICE_NOTE_LENGTH = 1000
export const MAX_KNOWLEDGE_BASE_TITLE_LENGTH = 300
export const MAX_KNOWLEDGE_BASE_DESCRIPTION_LENGTH = 3000
export const MAX_NON_RPM_DEVICE_TYPE_LENGTH = 255
export const MAX_NON_RPM_DEVICE_MODEL_ID_LENGTH = 255
export const MAX_NON_RPM_DEVICE_MANUFACTURER_LENGTH = 255
export const KNOWLEDGE_BASE_SUPPORTED_LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'Hmong', value: 'hmn' },
  { label: 'Russian', value: 'ru' },
  { label: 'Somali', value: 'so' },
  { label: 'Spanish', value: 'es' },
  { label: 'Vietnamese', value: 'vi' }
]

export const ASSIGN_DEVICE_TYPE_OPTIONS = [
  {
    label: 'Dose Device',
    value: 'MEDICATION_DISPENSER'
  },
  {
    label: 'Thermometer',
    value: 'THERMOMETER',
  },
  {
    label: 'Pulse Oximeter',
    value: 'PULSE_OXIMETER',
  },
  {
    label: 'Blood Pressure',
    value: 'BLOOD_PRESSURE',
  },
  {
    label: 'Weight Scale',
    value: 'WEIGHT_SCALE',
  },
  {
    label: 'Glucometer',
    value: 'GLUCOMETER',
  }
]

export const FILTER_DEVICE_TYPE_OPTIONS = [
  {
    label: 'All Types',
    value: 'ALL'
  },
  {
    label: 'Dose Device',
    value: 'MEDICATION_DISPENSER'
  },
  {
    label: 'Thermometer',
    value: 'THERMOMETER',
  },
  {
    label: 'Pulse Oximeter',
    value: 'PULSE_OXIMETER',
  },
  {
    label: 'Blood Pressure',
    value: 'BLOOD_PRESSURE',
  },
  {
    label: 'Weight Scale',
    value: 'WEIGHT_SCALE',
  },
  {
    label: 'Glucometer',
    value: 'GLUCOMETER',
  }
]

export enum DeviceOption {
  RPM_DEVICE = 'rpm_device',
  NON_RPM_DEVICE = 'non_rpm_device'
}

export const DEVICE_OPTIONS = [
  {
    label: 'RPM Device',
    value: DeviceOption.RPM_DEVICE
  },
  {
    label: 'Non-RPM Device',
    value: DeviceOption.NON_RPM_DEVICE
  }
]

export const DEVICE_STATUS_OPTIONS = [
  {
    label: 'Active',
    value: 'ACTIVE'
  },
  {
    label: 'Available',
    value: 'AVAILABLE'
  },
  {
    label: 'Unavailable',
    value: 'UNAVAILABLE'
  }
]

export const DEVICE_LOCATION_OPTIONS = [
  {
    label: 'Warehouse',
    value: 'WAREHOUSE'
  },
  {
    label: 'Client Home',
    value: 'CLIENT_HOME'
  },
  {
    label: 'In Transit',
    value: 'IN_TRANSIT'
  },
  {
    label: 'Unknown',
    value: 'UNKNOWN'
  }
]

export const DEVICE_LOCATION_BY_DEVICE_STATUS = [
  {
    deviceStatus: 'ACTIVE',
    deviceLocationOptions: [
      {
        label: 'Warehouse',
        value: 'WAREHOUSE',
        disabled: true
      },
      {
        label: 'Client Home',
        value: 'CLIENT_HOME',
        disabled: false
      },
      {
        label: 'In Transit',
        value: 'IN_TRANSIT',
        disabled: true
      },
      {
        label: 'Unknown',
        value: 'UNKNOWN',
        disabled: true
      }
    ]
  }, {
    deviceStatus: 'AVAILABLE',
    deviceLocationOptions: [
      {
        label: 'Warehouse',
        value: 'WAREHOUSE',
        disabled: false
      },
      {
        label: 'Client Home',
        value: 'CLIENT_HOME',
        disabled: true
      },
      {
        label: 'In Transit',
        value: 'IN_TRANSIT',
        disabled: true
      },
      {
        label: 'Unknown',
        value: 'UNKNOWN',
        disabled: true
      }
    ]
  }, {
    deviceStatus: 'UNAVAILABLE',
    deviceLocationOptions: [
      {
        label: 'Warehouse',
        value: 'WAREHOUSE',
        disabled: true
      },
      {
        label: 'Client Home',
        value: 'CLIENT_HOME',
        disabled: true
      },
      {
        label: 'In Transit',
        value: 'IN_TRANSIT',
        disabled: false
      },
      {
        label: 'Unknown',
        value: 'UNKNOWN',
        disabled: false
      }
    ]
  }
]
export const MAX_NOTE_CHAR = 140

// Device models
export const DEVICE_MODELS_FILTER_OPTIONS = [
  {
    label: 'By Device Type',
    value: 'type'
  },
  {
    label: 'By Model ID',
    value: 'modelId'
  },
  {
    label: 'By Vendor',
    value: 'vendor'
  }
]

export const HOME_PAGE_STATICS = {
  ALL: 'Total',
  VITALS: 'Vitals',
  MEDICATIONS: 'Missed Medications',
  COMPLIANCE: 'Missed Device Compliance',
  NO_REMINDER_SET: 'No Reminders Set'
}

export const HOME_PAGE_STATICS_TABS = {
  ALL: { label: HOME_PAGE_STATICS.ALL, hash: 'all' },
  VITALS: { label: HOME_PAGE_STATICS.VITALS, hash: 'vitals' },
  MEDICATION: { label: HOME_PAGE_STATICS.MEDICATIONS, hash: 'medications' },
  COMPLIANCE: { label: HOME_PAGE_STATICS.COMPLIANCE, hash: 'compliance' },
  NO_REMIDNER_SET: { label: HOME_PAGE_STATICS.NO_REMINDER_SET, hash: 'no_reminder_set' },
}

export const MIN_INVENTORY_COUNT = 1
export const MAX_SERIAL_LENGTH = 100
export const MAX_INVENTORY_COUNT = 100
export const MAX_MAC_ADD_LENGTH = 100
export const MAX_DOSE_ID_LENGTH = 100

export const DEVICE_CREATE_SUCCESS_MSG = 'Your device has been created successfully'
export const DEVICE_DELETE_SUCCESS_MSG = 'Your device has been deleted successfully!'

export const DOSE_DEVICE_REMINDER_WARNING =
  'Medication Reminder has been scheduled for this device. It will un-publish the associated reminder schedule and not count in Medication Score until you create a new schedule.'

export const KNOWLEDGE_BASE_DELETE_SUCCESS_MSG = 'Your entry has been deleted successfully!'
export const KNOWLEDGE_BASE_CREATE_SUCCESS_MSG = 'Your entry has been created successfully!'
export const KNOWLEDGE_BASE_MOVE_SUCCESS_MSG = 'Your entry has been moved successfully!'
export const NEW_DEVICE_ERROR_MSG = 'Please only enter whole numbers between 1 - 100'
export const DEVICE_GOALS_LIMIT_ERROR_MSG = 'Please enter a valid integer number from 1 to 28.'
export const ASSIGN_DEVICE_ERROR_MSG = 'There is an issue when proceeding your request. Please try again.'

export const convertDeviceType = (type: string | undefined) => {
  const MEDICATION_DISPENSER = 'MEDICATION_DISPENSER'
  const MEDICATION_DISPENSER_ALT_NAME = 'DOSE_DEVICE'

  if (type === MEDICATION_DISPENSER) {
    return MEDICATION_DISPENSER_ALT_NAME
  }
  return type
}
export const getHelpMessage = (meta: FieldMetaProps<any>) => {
  if (meta.touched) {
    if (isArray(meta.error)) {
      return meta.error.filter((e) => !!e)[0]
    }
    return meta.error
  }
  return null
}


export const getStatusBadeColor = (status: string) => {
  const ACTIVE = 'ACTIVE'
  const AVAILABLE = 'AVAILABLE'
  const UNAVAILABLE = 'UNAVAILABLE'

  switch (toUpper(status)) {
    case ACTIVE:
      return GREEN_600
    case AVAILABLE:
      return BLUE_600
    case UNAVAILABLE:
      return GRAY_300
    default:
      return GRAY_300
  }
}

export const PAGINATION_ASSIGN_VIDEO = {
  VIEW_ALL_CLIENTS: 25,
  OTHER: 1000 * 1000
}

// client detail - compliance
export const NON_COMPLIANCE = 'Non-Compliance'

export const DOSE_DEVICE_EVENT_TYPE: { [key: string]: string } = {
  Refilled: 'Device Refilled',
  NeedsRefilled: 'Empty Device',
  Dispensed: 'Medications Dispensed',
  Missed: 'Medications Missed',
  LidSecured: 'Device Lid Secured',
  LidRemoved: 'Device Lid Removed',
  PluggedIn: 'Device Plugged Into Power',
  PluggedOut: 'Device Unplugged',
  TravelModeActivated: '“Travel Mode” Activated',
  LowBattery: 'Low battery',
  MotorObstructed: 'Device Motor Obstructed',
  Notify0Minutes: 'Medication Reminder',
  Notify30Minutes: 'No Med Dispense After 30 Minutes',
  Notify60Minutes: 'No Med Dispense After 60 Minutes',
  Notify90Minutes: 'No Med Dispense After 90 Minutes',
  NotifyEmpty30Minutes: 'Empty Device With Upcoming Med Reminder',
  TrayPushed: 'Tray Pushed',
}

// medication reminder
export const FREQUENCY_REMINDER_OPTIONS = [
  {
    label: 'Day(s)',
    value: 'day',
  },
  {
    label: 'Week(s)',
    value: 'week',
  },
  {
    label: 'Month(s)',
    value: 'month',
  },
]

export const MIN_MONTH_FREQ = 1
export const MAX_MONTH_FREQ = 5

/** Shown under day grid; Yup `.min(1, …)` must use the same string. */
export const MONTH_SELECTED_DATES_AT_LEAST_ONE_MSG =
  'Please select at least one day of the month.'

export const REMINDER_ON_OPTIONS = [
  { label: 'Selected Date(s)', value: 'SELECTED_DATES' },
  { label: 'A Specific Day', value: 'SPECIFIC_DAY' },
] as const

export const ORDINAL_OPTIONS = [
  { label: 'First', value: 'FIRST' },
  { label: 'Second', value: 'SECOND' },
  { label: 'Third', value: 'THIRD' },
  { label: 'Fourth', value: 'FOURTH' },
  { label: 'Last', value: 'LAST' },
] as const

export const MONTHLY_WEEKDAY_OPTIONS = [
  { label: 'Monday', value: 'MONDAY' },
  { label: 'Tuesday', value: 'TUESDAY' },
  { label: 'Wednesday', value: 'WEDNESDAY' },
  { label: 'Thursday', value: 'THURSDAY' },
  { label: 'Friday', value: 'FRIDAY' },
  { label: 'Saturday', value: 'SATURDAY' },
  { label: 'Sunday', value: 'SUNDAY' },
] as const

export const DAY_OF_WEEK = [
  {
    label: 'Sun',
    value: '1',
  },
  {
    label: 'Mon',
    value: '2',
  },
  {
    label: 'Tue',
    value: '3',
  },
  {
    label: 'Wed',
    value: '4',
  },
  {
    label: 'Thu',
    value: '5',
  },
  {
    label: 'Fri',
    value: '6',
  },
  {
    label: 'Sat',
    value: '7',
  },
]

export const MIN_DAY_FREQUENCY_ERROR_MSG = 'The minimum value of this field is 1.'
export const MAX_DAY_FREQUENCY_ERROR_MSG = 'The maximum value of this field is 6.'
export const MIN_WEEK_FREQUENCY_ERROR_MSG = 'The minimum value of this field is 1.'
export const MAX_WEEK_FREQUENCY_ERROR_MSG = 'The maximum value of this field is 5.'
export const MIN_MONTH_FREQUENCY_ERROR_MSG = 'The minimum value of this field is 1.'
export const MAX_MONTH_FREQUENCY_ERROR_MSG = 'The maximum value of this field is 3.'
export const DAY_OF_MONTH_REQUIRED_MSG = 'Please select one date of the month.'
export const DAY_OF_MONTH_RANGE_ERROR_MSG = 'Day of month must be between 1 and 31.'

export const MONTHLY_SPECIFIC_DAY_INSTRUCTION =
  'Please select a specific day of the month to generate reminder'

export const TIMEZONE = [
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Addis_Ababa',
  'Africa/Algiers',
  'Africa/Asmara',
  'Africa/Asmera',
  'Africa/Bamako',
  'Africa/Bangui',
  'Africa/Banjul',
  'Africa/Bissau',
  'Africa/Blantyre',
  'Africa/Brazzaville',
  'Africa/Bujumbura',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Ceuta',
  'Africa/Conakry',
  'Africa/Dakar',
  'Africa/Dar_es_Salaam',
  'Africa/Djibouti',
  'Africa/Douala',
  'Africa/El_Aaiun',
  'Africa/Freetown',
  'Africa/Gaborone',
  'Africa/Harare',
  'Africa/Johannesburg',
  'Africa/Juba',
  'Africa/Kampala',
  'Africa/Khartoum',
  'Africa/Kigali',
  'Africa/Kinshasa',
  'Africa/Lagos',
  'Africa/Libreville',
  'Africa/Lome',
  'Africa/Luanda',
  'Africa/Lubumbashi',
  'Africa/Lusaka',
  'Africa/Malabo',
  'Africa/Maputo',
  'Africa/Maseru',
  'Africa/Mbabane',
  'Africa/Mogadishu',
  'Africa/Monrovia',
  'Africa/Nairobi',
  'Africa/Ndjamena',
  'Africa/Niamey',
  'Africa/Nouakchott',
  'Africa/Ouagadougou',
  'Africa/Porto-Novo',
  'Africa/Sao_Tome',
  'Africa/Timbuktu',
  'Africa/Tripoli',
  'Africa/Tunis',
  'Africa/Windhoek',
  'America/Adak',
  'America/Anchorage',
  'America/Anguilla',
  'America/Antigua',
  'America/Araguaina',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/ComodRivadavia',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/La_Rioja',
  'America/Argentina/Mendoza',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Salta',
  'America/Argentina/San_Juan',
  'America/Argentina/San_Luis',
  'America/Argentina/Tucuman',
  'America/Argentina/Ushuaia',
  'America/Aruba',
  'America/Asuncion',
  'America/Atikokan',
  'America/Atka',
  'America/Bahia',
  'America/Bahia_Banderas',
  'America/Barbados',
  'America/Belem',
  'America/Belize',
  'America/Blanc-Sablon',
  'America/Boa_Vista',
  'America/Bogota',
  'America/Boise',
  'America/Buenos_Aires',
  'America/Cambridge_Bay',
  'America/Campo_Grande',
  'America/Cancun',
  'America/Caracas',
  'America/Catamarca',
  'America/Cayenne',
  'America/Cayman',
  'America/Chicago',
  'America/Chihuahua',
  'America/Coral_Harbour',
  'America/Cordoba',
  'America/Costa_Rica',
  'America/Creston',
  'America/Cuiaba',
  'America/Curacao',
  'America/Danmarkshavn',
  'America/Dawson',
  'America/Dawson_Creek',
  'America/Denver',
  'America/Detroit',
  'America/Dominica',
  'America/Edmonton',
  'America/Eirunepe',
  'America/El_Salvador',
  'America/Ensenada',
  'America/Fort_Nelson',
  'America/Fort_Wayne',
  'America/Fortaleza',
  'America/Glace_Bay',
  'America/Godthab',
  'America/Goose_Bay',
  'America/Grand_Turk',
  'America/Grenada',
  'America/Guadeloupe',
  'America/Guatemala',
  'America/Guayaquil',
  'America/Guyana',
  'America/Halifax',
  'America/Havana',
  'America/Hermosillo',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indianapolis',
  'America/Inuvik',
  'America/Iqaluit',
  'America/Jamaica',
  'America/Jujuy',
  'America/Juneau',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Knox_IN',
  'America/Kralendijk',
  'America/La_Paz',
  'America/Lima',
  'America/Los_Angeles',
  'America/Louisville',
  'America/Lower_Princes',
  'America/Maceio',
  'America/Managua',
  'America/Manaus',
  'America/Marigot',
  'America/Martinique',
  'America/Matamoros',
  'America/Mazatlan',
  'America/Mendoza',
  'America/Menominee',
  'America/Merida',
  'America/Metlakatla',
  'America/Mexico_City',
  'America/Miquelon',
  'America/Moncton',
  'America/Monterrey',
  'America/Montevideo',
  'America/Montreal',
  'America/Montserrat',
  'America/Nassau',
  'America/New_York',
  'America/Nipigon',
  'America/Nome',
  'America/Noronha',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/Nuuk',
  'America/Ojinaga',
  'America/Panama',
  'America/Pangnirtung',
  'America/Paramaribo',
  'America/Phoenix',
  'America/Port-au-Prince',
  'America/Port_of_Spain',
  'America/Porto_Acre',
  'America/Porto_Velho',
  'America/Puerto_Rico',
  'America/Punta_Arenas',
  'America/Rainy_River',
  'America/Rankin_Inlet',
  'America/Recife',
  'America/Regina',
  'America/Resolute',
  'America/Rio_Branco',
  'America/Rosario',
  'America/Santa_Isabel',
  'America/Santarem',
  'America/Santiago',
  'America/Santo_Domingo',
  'America/Sao_Paulo',
  'America/Scoresbysund',
  'America/Shiprock',
  'America/Sitka',
  'America/St_Barthelemy',
  'America/St_Johns',
  'America/St_Kitts',
  'America/St_Lucia',
  'America/St_Thomas',
  'America/St_Vincent',
  'America/Swift_Current',
  'America/Tegucigalpa',
  'America/Thule',
  'America/Thunder_Bay',
  'America/Tijuana',
  'America/Toronto',
  'America/Tortola',
  'America/Vancouver',
  'America/Virgin',
  'America/Whitehorse',
  'America/Winnipeg',
  'America/Yakutat',
  'America/Yellowknife',
  'Antarctica/Casey',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Antarctica/Macquarie',
  'Antarctica/Mawson',
  'Antarctica/McMurdo',
  'Antarctica/Palmer',
  'Antarctica/Rothera',
  'Antarctica/South_Pole',
  'Antarctica/Syowa',
  'Antarctica/Troll',
  'Antarctica/Vostok',
  'Arctic/Longyearbyen',
  'Asia/Aden',
  'Asia/Almaty',
  'Asia/Amman',
  'Asia/Anadyr',
  'Asia/Aqtau',
  'Asia/Aqtobe',
  'Asia/Ashgabat',
  'Asia/Ashkhabad',
  'Asia/Atyrau',
  'Asia/Baghdad',
  'Asia/Bahrain',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Barnaul',
  'Asia/Beirut',
  'Asia/Bishkek',
  'Asia/Brunei',
  'Asia/Calcutta',
  'Asia/Chita',
  'Asia/Choibalsan',
  'Asia/Chongqing',
  'Asia/Chungking',
  'Asia/Colombo',
  'Asia/Dacca',
  'Asia/Damascus',
  'Asia/Dhaka',
  'Asia/Dili',
  'Asia/Dubai',
  'Asia/Dushanbe',
  'Asia/Famagusta',
  'Asia/Gaza',
  'Asia/Harbin',
  'Asia/Hebron',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Hovd',
  'Asia/Irkutsk',
  'Asia/Istanbul',
  'Asia/Jakarta',
  'Asia/Jayapura',
  'Asia/Jerusalem',
  'Asia/Kabul',
  'Asia/Kamchatka',
  'Asia/Karachi',
  'Asia/Kashgar',
  'Asia/Kathmandu',
  'Asia/Katmandu',
  'Asia/Khandyga',
  'Asia/Kolkata',
  'Asia/Krasnoyarsk',
  'Asia/Kuala_Lumpur',
  'Asia/Kuching',
  'Asia/Kuwait',
  'Asia/Macao',
  'Asia/Macau',
  'Asia/Magadan',
  'Asia/Makassar',
  'Asia/Manila',
  'Asia/Muscat',
  'Asia/Nicosia',
  'Asia/Novokuznetsk',
  'Asia/Novosibirsk',
  'Asia/Omsk',
  'Asia/Oral',
  'Asia/Phnom_Penh',
  'Asia/Pontianak',
  'Asia/Pyongyang',
  'Asia/Qatar',
  'Asia/Qostanay',
  'Asia/Qyzylorda',
  'Asia/Rangoon',
  'Asia/Riyadh',
  'Asia/Saigon',
  'Asia/Sakhalin',
  'Asia/Samarkand',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Srednekolymsk',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tehran',
  'Asia/Tel_Aviv',
  'Asia/Thimbu',
  'Asia/Thimphu',
  'Asia/Tokyo',
  'Asia/Tomsk',
  'Asia/Ujung_Pandang',
  'Asia/Ulaanbaatar',
  'Asia/Ulan_Bator',
  'Asia/Urumqi',
  'Asia/Ust-Nera',
  'Asia/Vientiane',
  'Asia/Vladivostok',
  'Asia/Yakutsk',
  'Asia/Yangon',
  'Asia/Yekaterinburg',
  'Asia/Yerevan',
  'Atlantic/Azores',
  'Atlantic/Bermuda',
  'Atlantic/Canary',
  'Atlantic/Cape_Verde',
  'Atlantic/Faeroe',
  'Atlantic/Faroe',
  'Atlantic/Jan_Mayen',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
  'Atlantic/South_Georgia',
  'Atlantic/St_Helena',
  'Atlantic/Stanley',
  'Australia/ACT',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Broken_Hill',
  'Australia/Canberra',
  'Australia/Currie',
  'Australia/Darwin',
  'Australia/Eucla',
  'Australia/Hobart',
  'Australia/LHI',
  'Australia/Lindeman',
  'Australia/Lord_Howe',
  'Australia/Melbourne',
  'Australia/NSW',
  'Australia/North',
  'Australia/Perth',
  'Australia/Queensland',
  'Australia/South',
  'Australia/Sydney',
  'Australia/Tasmania',
  'Australia/Victoria',
  'Australia/West',
  'Australia/Yancowinna',
  'Brazil/Acre',
  'Brazil/DeNoronha',
  'Brazil/East',
  'Brazil/West',
  'CET',
  'CST6CDT',
  'Canada/Atlantic',
  'Canada/Central',
  'Canada/Eastern',
  'Canada/Mountain',
  'Canada/Newfoundland',
  'Canada/Pacific',
  'Canada/Saskatchewan',
  'Canada/Yukon',
  'Chile/Continental',
  'Chile/EasterIsland',
  'Cuba',
  'EET',
  'EST5EDT',
  'Egypt',
  'Eire',
  'Etc/GMT',
  'Etc/GMT+0',
  'Etc/GMT+1',
  'Etc/GMT+10',
  'Etc/GMT+11',
  'Etc/GMT+12',
  'Etc/GMT+2',
  'Etc/GMT+3',
  'Etc/GMT+4',
  'Etc/GMT+5',
  'Etc/GMT+6',
  'Etc/GMT+7',
  'Etc/GMT+8',
  'Etc/GMT+9',
  'Etc/GMT-0',
  'Etc/GMT-1',
  'Etc/GMT-10',
  'Etc/GMT-11',
  'Etc/GMT-12',
  'Etc/GMT-13',
  'Etc/GMT-14',
  'Etc/GMT-2',
  'Etc/GMT-3',
  'Etc/GMT-4',
  'Etc/GMT-5',
  'Etc/GMT-6',
  'Etc/GMT-7',
  'Etc/GMT-8',
  'Etc/GMT-9',
  'Etc/GMT0',
  'Etc/Greenwich',
  'Etc/UCT',
  'Etc/UTC',
  'Etc/Universal',
  'Etc/Zulu',
  'Europe/Amsterdam',
  'Europe/Andorra',
  'Europe/Astrakhan',
  'Europe/Athens',
  'Europe/Belfast',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Bratislava',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Busingen',
  'Europe/Chisinau',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Gibraltar',
  'Europe/Guernsey',
  'Europe/Helsinki',
  'Europe/Isle_of_Man',
  'Europe/Istanbul',
  'Europe/Jersey',
  'Europe/Kaliningrad',
  'Europe/Kiev',
  'Europe/Kirov',
  'Europe/Lisbon',
  'Europe/Ljubljana',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Malta',
  'Europe/Mariehamn',
  'Europe/Minsk',
  'Europe/Monaco',
  'Europe/Moscow',
  'Europe/Nicosia',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Podgorica',
  'Europe/Prague',
  'Europe/Riga',
  'Europe/Rome',
  'Europe/Samara',
  'Europe/San_Marino',
  'Europe/Sarajevo',
  'Europe/Saratov',
  'Europe/Simferopol',
  'Europe/Skopje',
  'Europe/Sofia',
  'Europe/Stockholm',
  'Europe/Tallinn',
  'Europe/Tirane',
  'Europe/Tiraspol',
  'Europe/Ulyanovsk',
  'Europe/Uzhgorod',
  'Europe/Vaduz',
  'Europe/Vatican',
  'Europe/Vienna',
  'Europe/Vilnius',
  'Europe/Volgograd',
  'Europe/Warsaw',
  'Europe/Zagreb',
  'Europe/Zaporozhye',
  'Europe/Zurich',
  'GB',
  'GB-Eire',
  'GMT',
  'GMT+0',
  'GMT-0',
  'GMT0',
  'Greenwich',
  'Hongkong',
  'Iceland',
  'Indian/Antananarivo',
  'Indian/Chagos',
  'Indian/Christmas',
  'Indian/Cocos',
  'Indian/Comoro',
  'Indian/Kerguelen',
  'Indian/Mahe',
  'Indian/Maldives',
  'Indian/Mauritius',
  'Indian/Mayotte',
  'Indian/Reunion',
  'Iran',
  'Israel',
  'Jamaica',
  'Japan',
  'Kwajalein',
  'Libya',
  'MET',
  'MST7MDT',
  'Mexico/BajaNorte',
  'Mexico/BajaSur',
  'Mexico/General',
  'NZ',
  'NZ-CHAT',
  'Navajo',
  'PRC',
  'PST8PDT',
  'Pacific/Apia',
  'Pacific/Auckland',
  'Pacific/Bougainville',
  'Pacific/Chatham',
  'Pacific/Chuuk',
  'Pacific/Easter',
  'Pacific/Efate',
  'Pacific/Enderbury',
  'Pacific/Fakaofo',
  'Pacific/Fiji',
  'Pacific/Funafuti',
  'Pacific/Galapagos',
  'Pacific/Gambier',
  'Pacific/Guadalcanal',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Johnston',
  'Pacific/Kanton',
  'Pacific/Kiritimati',
  'Pacific/Kosrae',
  'Pacific/Kwajalein',
  'Pacific/Majuro',
  'Pacific/Marquesas',
  'Pacific/Midway',
  'Pacific/Nauru',
  'Pacific/Niue',
  'Pacific/Norfolk',
  'Pacific/Noumea',
  'Pacific/Pago_Pago',
  'Pacific/Palau',
  'Pacific/Pitcairn',
  'Pacific/Pohnpei',
  'Pacific/Ponape',
  'Pacific/Port_Moresby',
  'Pacific/Rarotonga',
  'Pacific/Saipan',
  'Pacific/Samoa',
  'Pacific/Tahiti',
  'Pacific/Tarawa',
  'Pacific/Tongatapu',
  'Pacific/Truk',
  'Pacific/Wake',
  'Pacific/Wallis',
  'Pacific/Yap',
  'Poland',
  'Portugal',
  'ROK',
  'Singapore',
  'Turkey',
  'UCT',
  'US/Alaska',
  'US/Aleutian',
  'US/Arizona',
  'US/Central',
  'US/East-Indiana',
  'US/Eastern',
  'US/Hawaii',
  'US/Indiana-Starke',
  'US/Michigan',
  'US/Mountain',
  'US/Pacific',
  'US/Samoa',
  'UTC',
  'Universal',
  'W-SU',
  'WET',
  'Zulu'
]
export const MONTH: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December'
}

export const poppins = Poppins({
  weight: ['400', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  variable: '--font-poppins',
})
